import { CommonModule } from '@angular/common';
import { Component, Input, output, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';

import { TranslatePipe } from '@core/i18n-legacy';
import { RequisitionsScope } from '@core/requisitions-types';
import { ButtonComponent, EmptyStateComponent, IconComponent } from '@core/user-interface-legacy';
import type { RequisitionSettingsNavigationExtraState } from '@recruitee/requisitions-settings-legacy';

enum EmptyStateKind {
  NoFlowsWithAccess = 'noFlowsWithAccess',
  NoFlowsWithoutAccess = 'noFlowsWithoutAccess',
  NoDefaultFlowWithAccess = 'noDefaultFlowWithAccess',
  NoDefaultFlowWithoutAccess = 'noDefaultFlowWithoutAccess',
  NoActiveRequisitionsWithAccess = 'noActiveRequisitionsWithAccess',
  NoActiveRequisitionsWithoutAccess = 'noActiveRequisitionsWithoutAccess',
  NoNeedMyApprovalRequisitions = 'noNeedMyApprovalRequisitions',
  NoRequestedByMeRequsitions = 'noRequestedByMeRequsitions',
  NoArchivedRequisitions = 'noArchivedRequisitions',
  NoRequisitionsFound = 'noRequisitionsFound',
}

@Component({
  selector: 'rt-requisitions-list-empty-state',
  templateUrl: './requisitions-list-empty-state.component.html',
  imports: [
    ButtonComponent,
    CommonModule,
    EmptyStateComponent,
    IconComponent,
    RouterModule,
    TranslatePipe,
  ],
})
export class RequisitionsListEmptyStateComponent {
  private readonly router = inject(Router);

  public readonly EmptyStateKind: typeof EmptyStateKind = EmptyStateKind;

  @Input() public canCreateRequisitions: boolean;
  @Input() public canManageRequisitionsSettings: boolean;
  @Input() public hasCreatedFlows: boolean;
  @Input() public isDefaultFlowMissing: boolean;
  @Input() public scope: RequisitionsScope;
  @Input() public isSearching: boolean;

  public newRequisition = output<void>();

  public emptyState: EmptyStateKind | null;

  public ngOnChanges(): void {
    this.emptyState = this.getEmptyState();
  }

  public openSettings() {
    const state: RequisitionSettingsNavigationExtraState = {
      addFlow: true,
    };
    this.router.navigate(['/settings/requisition_approvals/flows'], {
      state,
    });
  }

  private getEmptyState(): EmptyStateKind | null {
    if (this.isSearching) {
      return EmptyStateKind.NoRequisitionsFound;
    }

    if (this.scope === RequisitionsScope.Active) {
      if (!this.hasCreatedFlows) {
        return this.canManageRequisitionsSettings
          ? EmptyStateKind.NoFlowsWithAccess
          : EmptyStateKind.NoFlowsWithoutAccess;
      }
      if (this.isDefaultFlowMissing) {
        return this.canManageRequisitionsSettings
          ? EmptyStateKind.NoDefaultFlowWithAccess
          : EmptyStateKind.NoDefaultFlowWithoutAccess;
      }

      return this.canCreateRequisitions
        ? EmptyStateKind.NoActiveRequisitionsWithAccess
        : EmptyStateKind.NoActiveRequisitionsWithoutAccess;
    }

    if (this.scope === RequisitionsScope.NeedMyApproval) {
      return EmptyStateKind.NoNeedMyApprovalRequisitions;
    }

    if (this.scope === RequisitionsScope.RequestedByMe) {
      return EmptyStateKind.NoRequestedByMeRequsitions;
    }

    if (this.scope === RequisitionsScope.Archived) {
      return EmptyStateKind.NoArchivedRequisitions;
    }

    return null;
  }
}
